import React, { useState, useMemo, useEffect } from "react";
import { eventTypes, eventTypeLabel } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { ChevronDown, Search, Check, Clock } from "lucide-react";

function _slug(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

// Flat, alphabetized list of every event type — no default group highlighted.
const ALL_TYPES = [...eventTypes].sort((a, b) => a.label.localeCompare(b.label));

// Rotating placeholder examples so the search bar hints at variety.
const PLACEHOLDER_EXAMPLES = ["wedding", "birthday party", "corporate offsite", "fundraiser gala", "block party"];

// Recently-used event types — persisted per-browser (per-planner).
const RECENT_KEY = "eventlab.recent_event_types";
const RECENT_MAX = 5;

function readRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

function pushRecent(value) {
  try {
    const current = readRecent().filter(v => v !== value);
    const next = [value, ...current].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* ignore quota / privacy-mode errors */ }
}

export default function EventTypePicker({ value, onChange, testId = "event-type-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(readRecent);

  // Refresh recent list every time the picker opens (multiple pickers on the page share the same storage).
  useEffect(() => {
    if (open) setRecent(readRecent());
  }, [open]);

  // Pick a random example each time the picker opens so the placeholder feels alive.
  // `open` is intentional — Math.random() must re-run when the popover reopens.
  const placeholder = useMemo(
    () => `e.g. ${PLACEHOLDER_EXAMPLES[Math.floor(Math.random() * PLACEHOLDER_EXAMPLES.length)]}`,
    [open] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return ALL_TYPES;
    return ALL_TYPES.filter(t => t.label.toLowerCase().includes(query));
  }, [q]);

  // Resolve recent slugs → {value,label} (skip any legacy/removed slugs).
  const recentResolved = useMemo(() => {
    return recent
      .map(slug => ALL_TYPES.find(t => t.value === slug) || { value: slug, label: eventTypeLabel(slug) })
      .filter(Boolean);
  }, [recent]);

  const showRecent = !q.trim() && recentResolved.length > 0;
  const label = value ? eventTypeLabel(value) : "Choose event type";

  const commit = (val) => {
    pushRecent(val);
    setRecent(readRecent());
    onChange(val);
    setOpen(false);
    setQ("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          className="w-full justify-between rounded-sm border-hair bg-surface text-app font-normal h-10"
        >
          <span className="truncate">{label}</span>
          <ChevronDown size={14} strokeWidth={1.5} className="text-muted shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] max-w-[420px] p-0 bg-surface border-hair rounded-sm">
        <div className="border-b border-hair px-3 py-2 flex items-center gap-2">
          <Search size={14} strokeWidth={1.5} className="text-muted" />
          <input
            autoFocus
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-transparent outline-none text-sm placeholder:text-muted"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testId}-list`}>
          {showRecent && (
            <>
              <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 font-mono text-[10px] tracking-[0.2em] uppercase text-muted">
                <Clock size={11} strokeWidth={2} />
                Recent
              </div>
              {recentResolved.map(t => {
                const active = value === t.value;
                return (
                  <button
                    key={`recent-${t.value}`}
                    type="button"
                    data-testid={`event-type-recent-${t.value}`}
                    onClick={() => commit(t.value)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                      active ? "bg-surface-2 text-app" : "text-app hover:bg-canvas"
                    }`}
                  >
                    <span>{t.label}</span>
                    {active && <Check size={14} strokeWidth={1.5} className="text-app" />}
                  </button>
                );
              })}
              <div className="my-1 border-t border-hair" />
              <div className="px-3 pt-1 pb-1 font-mono text-[10px] tracking-[0.2em] uppercase text-muted">All types</div>
            </>
          )}

          {filtered.length === 0 && !q.trim() && (
            <div className="p-6 text-center text-sm text-muted">No event types.</div>
          )}
          {filtered.length === 0 && q.trim() && (
            <div className="p-4 text-center text-sm text-muted">No matches. Use the custom option below.</div>
          )}
          {filtered.map(t => {
            const active = value === t.value;
            return (
              <button
                key={t.value}
                type="button"
                data-testid={`event-type-option-${t.value}`}
                onClick={() => commit(t.value)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                  active ? "bg-surface-2 text-app" : "text-app hover:bg-canvas"
                }`}
              >
                <span>{t.label}</span>
                {active && <Check size={14} strokeWidth={1.5} className="text-app" />}
              </button>
            );
          })}
          {q.trim() && !ALL_TYPES.some(t => t.label.toLowerCase() === q.trim().toLowerCase()) && (
            <button
              type="button"
              data-testid="event-type-custom"
              onClick={() => commit(_slug(q.trim()))}
              className="w-full text-left px-3 py-2 border-t border-hair text-sm text-app hover:bg-canvas"
            >
              Use custom type: <span className="font-medium">{q.trim()}</span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
